<template>
  <div class="c-font-size-select inline-flex items-center">
    <CAutoComplete
      ref="autocompleteRef"
      v-model:value="fontSize"
      :bordered="true"
      :consistent-menu-width="false"
      :filterable="false"
      :getShow="() => true"
      :inputProps="{
        style: {
          textAlign: 'center',
          width: '28px',
        },
        class: 'font-500',
      }"
      :menuProps="{
        style: 'width: 75px',
      }"
      :options="fontSizeOptions"
      placeholder=""
      :renderLabel="renderLabel"
      :themeOverrides="{
        peers: {
          Input: {
            textColor: themeVars.primary,
            color: 'transparent',
            colorFocus: 'transparent',
            border: '1px solid transparent',
            borderFocus: `1px solid ${themeVars.primary5}`,
          },
        },
      }"
      @blur="onBlur"
      @click.stop="handleClick"
      @select="updateFontSizeOnDOM($event)"
    />

    <div class="flex flex-col justify-center">
      <CIcon
        :class="{
          'cursor-pointer': !isFontSizeTooBig,
        }"
        :color="isFontSizeTooBig ? themeVars.primary4 : themeVars.primary"
        icon="angle-up"
        role="button"
        :size="12"
        @click="setBiggerFontSize()"
      />

      <CIcon
        :class="{
          'cursor-pointer': !isFontSizeTooSmall,
        }"
        :color="isFontSizeTooSmall ? themeVars.primary4 : themeVars.primary"
        icon="angle-down"
        role="button"
        :size="12"
        @click="setSmallerFontSize()"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, type Ref, type VNode } from 'vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { DEFAULT_FONT_SIZE } from '@lib/components/CRichTextEditor/extensions/paragraphExtended'
import useThemeVars from '@lib/composables/useThemeVars'
import type { Editor } from '@tiptap/vue-3'
import CAutoComplete from '@lib/components/CAutoComplete/CAutoComplete.vue'
import type { Option } from '@lib/components/CRichTextEditor/components/CEditorMenu.vue'
import { fontSizePresets as sizeOptions } from '@theme/theme.utils'

interface Props {
  editor: Editor
  renderLabel: (option: Option) => VNode
  value?: string
}

const props = defineProps<Props>()
const themeVars = useThemeVars()

const defaultFontSize: string = DEFAULT_FONT_SIZE.toString() // styling[Paragraphs.NORMAL_TEXT].fontSize.toString()
const fontSize: Ref<string> = ref(props.value || props.editor.getAttributes('textStyle').fontSize || defaultFontSize)
const autocompleteRef = ref()

const fontSizeAsNumber = (v: string | number = fontSize.value) => {
  const value = typeof v === 'string' ? Number.parseInt(v) : v

  return {
    value,
    isInteger: Number.isInteger(value),
  }
}

const setSmallerFontSize = () => {
  const revered = [...sizeOptions].reverse()
  const { value, isInteger } = fontSizeAsNumber()

  if (!isInteger) return

  if (value > sizeOptions[0]) {
    for (const i of revered) {
      if (i < value) {
        updateFontSizeOnDOM(i)
        break
      }
    }
  }
}

const updateFontSize = (value: string | number) => {
  fontSize.value = typeof value === 'string' ? value : value.toString()
}

const validateFontSize = (v: string) => {
  const { isInteger, value } = fontSizeAsNumber(v)

  if (!isInteger || value <= 0)
    fontSize.value = props.editor.getAttributes('textStyle').fontSize || props.value || defaultFontSize
}

const isFontSizeTooBig = computed(() => {
  const { value, isInteger } = fontSizeAsNumber()
  return !isInteger || value >= sizeOptions[sizeOptions.length - 1]
})

const isFontSizeTooSmall = computed(() => {
  const { value, isInteger } = fontSizeAsNumber()
  return !isInteger || value <= sizeOptions[0]
})

const setBiggerFontSize = () => {
  const { value, isInteger } = fontSizeAsNumber()
  if (!isInteger) return

  if (value < sizeOptions[sizeOptions.length - 1]) {
    for (const i of sizeOptions) {
      if (i > value) {
        updateFontSizeOnDOM(i)
        break
      }
    }
  }
}

watch(
  [() => props.editor.getAttributes('textStyle').fontSize, () => props.value],
  ([editorFontSize, propsValue]) => {
    if (propsValue) {
      updateFontSize(propsValue)
    } else if (editorFontSize) {
      const value = editorFontSize || defaultFontSize
      updateFontSize(typeof value === 'string' ? Number.parseInt(value) : value)
    } else {
      updateFontSize(defaultFontSize)
    }
  },
  { immediate: true },
)

const updateFontSizeOnDOM = (size: number | string) => {
  const { isInteger, value } = fontSizeAsNumber(size)

  if (isInteger) {
    props.editor.chain().focus().setFontSize(value.toString()).run()
  }
}

const onBlur = () => {
  validateFontSize(fontSize.value)
  updateFontSizeOnDOM(fontSize.value)
}

function handleClick() {
  autocompleteRef.value && autocompleteRef.value.$el.getElementsByTagName('input')[0].focus()
}

const fontSizeOptions = computed(() => {
  let options = [...sizeOptions]
  const { value, isInteger } = fontSizeAsNumber()

  if (isInteger) {
    if (sizeOptions.includes(value)) {
      options = options.filter((i) => i !== value)
    }

    options.unshift(value)
  }

  return options.map((item) => ({
    label: item.toString(),
    value: item,
  }))
})
</script>
<style scoped>
.c-font-size-select :deep(.n-input-wrapper) {
  padding-left: 6px;
  padding-right: 6px;
}
</style>
